/**
 * Import Dialog
 *
 * Dialog for importing agents, teams, or workflows from JSON
 */
import { useState, useRef } from 'react'
import { XMarkIcon, ArrowUpTrayIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import Button from './Button'
import Card from './Card'
import type { ImportResult } from '@/services/importService'

interface ImportDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  itemType: string
  onImport: (file: File, options: ImportOptions) => Promise<ImportResult<any>>
  onConfirm?: (data: any[]) => Promise<void>
}

export interface ImportOptions {
  skipInvalid: boolean
  validateOnly: boolean
}

type Step = 'upload' | 'preview' | 'importing' | 'complete'

export default function ImportDialog({
  isOpen,
  onClose,
  title,
  itemType,
  onImport,
  onConfirm,
}: ImportDialogProps) {
  const [step, setStep] = useState<Step>('upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ImportResult<any> | null>(null)
  const [options, setOptions] = useState<ImportOptions>({
    skipInvalid: true,
    validateOnly: false,
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!isOpen) return null

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setSelectedFile(file)
      } else {
        alert('Please select a JSON file')
      }
    }
  }

  const handleValidate = async () => {
    if (!selectedFile) return

    setIsProcessing(true)
    try {
      const result = await onImport(selectedFile, { ...options, validateOnly: true })
      setImportResult(result)
      setStep('preview')
    } catch (error: any) {
      alert(error.message || 'Failed to validate file')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleImport = async () => {
    if (!selectedFile || !importResult) return

    setStep('importing')
    setIsProcessing(true)

    try {
      const result = await onImport(selectedFile, { ...options, validateOnly: false })

      if (result.success && result.data && onConfirm) {
        await onConfirm(result.data)
      }

      setImportResult(result)
      setStep('complete')
    } catch (error: any) {
      alert(error.message || 'Import failed')
      setStep('preview')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReset = () => {
    setStep('upload')
    setSelectedFile(null)
    setImportResult(null)
    setIsProcessing(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-foreground">{title}</h2>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-muted-foreground p-2"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
              <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-foreground font-medium mb-2">
                Select JSON file to import
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a JSON file containing {itemType} data
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="primary"
              >
                Choose File
              </Button>
            </div>

            {selectedFile && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-green-600">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
              </div>
            )}

            {/* Options */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Import Options
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.skipInvalid}
                    onChange={(e) =>
                      setOptions({ ...options, skipInvalid: e.target.checked })
                    }
                    className="w-4 h-4 text-primary-600 border-border rounded"
                  />
                  <span className="ml-2 text-sm text-foreground">
                    Skip invalid items (import only valid items)
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleValidate}
                variant="primary"
                className="flex-1"
                disabled={!selectedFile || isProcessing}
                isLoading={isProcessing}
              >
                Validate & Preview
              </Button>
              <Button onClick={handleClose} variant="secondary" className="sm:w-auto">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && importResult && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <p className="text-xs text-green-600 mb-1">Valid Items</p>
                <p className="text-2xl font-bold text-green-600">
                  {importResult.imported}
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-xs text-red-600 mb-1">Invalid Items</p>
                <p className="text-2xl font-bold text-red-600">
                  {importResult.skipped}
                </p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-xs text-yellow-600 mb-1">Warnings</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {importResult.warnings.length}
                </p>
              </div>
            </div>

            {/* Errors */}
            {importResult.errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircleIcon className="w-5 h-5 text-red-600" />
                  <h4 className="font-semibold text-red-600">
                    Errors ({importResult.errors.length})
                  </h4>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.slice(0, 10).map((error, idx) => (
                    <p key={idx} className="text-sm text-red-600">
                      • {error.message}
                    </p>
                  ))}
                  {importResult.errors.length > 10 && (
                    <p className="text-sm text-red-600 font-medium">
                      ... and {importResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Warnings */}
            {importResult.warnings.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-semibold text-yellow-600">
                    Warnings ({importResult.warnings.length})
                  </h4>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {importResult.warnings.slice(0, 10).map((warning, idx) => (
                    <p key={idx} className="text-sm text-yellow-600">
                      • {warning}
                    </p>
                  ))}
                  {importResult.warnings.length > 10 && (
                    <p className="text-sm text-yellow-600 font-medium">
                      ... and {importResult.warnings.length - 10} more warnings
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleImport}
                variant="primary"
                className="flex-1"
                disabled={importResult.imported === 0 || isProcessing}
                isLoading={isProcessing}
              >
                Import {importResult.imported} {itemType}
                {importResult.imported !== 1 ? 's' : ''}
              </Button>
              <Button onClick={handleReset} variant="secondary" className="sm:w-auto">
                Back
              </Button>
              <Button onClick={handleClose} variant="secondary" className="sm:w-auto">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-foreground font-medium">Importing {itemType}s...</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait</p>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && importResult && (
          <div className="space-y-4">
            <div className="text-center py-6">
              <CheckCircleIcon className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Import Complete!
              </h3>
              <p className="text-muted-foreground">
                Successfully imported {importResult.imported} {itemType}
                {importResult.imported !== 1 ? 's' : ''}
              </p>
            </div>

            {importResult.skipped > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-yellow-600">
                  {importResult.skipped} item{importResult.skipped !== 1 ? 's were' : ' was'}{' '}
                  skipped due to validation errors
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleClose} variant="primary" className="flex-1">
                Done
              </Button>
              <Button onClick={handleReset} variant="secondary" className="sm:w-auto">
                Import More
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
